import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useSimpleToast } from '../hooks/useToast'
import { Skeleton } from '../components/ui/skeleton'
import { PlanWizard } from '../components/training-plans/PlanWizard'
import {
  emptyPlanForm,
  planToForm,
  formToPayload,
  type PlanFormData,
} from '../components/training-plans/plan-helpers'
import { trainingPlansService } from '../services/training-plans'
import type { PlanUserSummary } from '../types/training-plan'

/** Coach passed via navigation state from the coach's secret invite link. */
interface InviteState {
  coach?: PlanUserSummary
  coachToken?: string
}

/**
 * Hosts the questionnaire wizard for creating a plan (no id) and editing an
 * existing plan's details (with id). The athlete is always the current user;
 * when launched from a coach's profile the coach arrives via navigation state,
 * and on submit the plan is created as a pending invitation for that coach.
 */
export default function TrainingPlanFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const { showSuccess, showError } = useSimpleToast()

  const isEdit = Boolean(id)
  const inviteState = location.state as InviteState | null
  const coach = inviteState?.coach ?? null
  const coachToken = inviteState?.coachToken ?? null
  const [initialData, setInitialData] = useState<PlanFormData | null>(isEdit ? null : emptyPlanForm())
  const [submitting, setSubmitting] = useState(false)

  // In edit mode the last crumb links back to the plan; in create mode it's the
  // (current) "new plan" page.
  const planLabel = initialData
    ? initialData.goal.trim() || t(`trainingPlans.discipline.${initialData.discipline}`)
    : t('trainingPlans.builder.title')

  useBreadcrumb(
    () => ({
      onBack: () => navigate(isEdit ? `/training-plans/${id}` : '/training-plans'),
      segments: isEdit
        ? [
            { label: t('trainingPlans.pageTitle'), onClick: () => navigate('/training-plans') },
            { label: planLabel, onClick: () => navigate(`/training-plans/${id}`) },
          ]
        : [
            { label: t('trainingPlans.pageTitle'), onClick: () => navigate('/training-plans') },
            { label: t('trainingPlans.wizard.title') },
          ],
    }),
    [t, navigate, id, isEdit, planLabel]
  )

  useEffect(() => {
    if (!isEdit || !id) return
    trainingPlansService
      .get(id)
      .then((plan) => setInitialData(planToForm(plan)))
      .catch(() => showError(t('trainingPlans.builder.loadError')))
  }, [id, isEdit, showError, t])

  async function handleSubmit(form: PlanFormData) {
    setSubmitting(true)
    try {
      const payload = formToPayload(form)
      if (isEdit && id) {
        // Editing details never regenerates the diary on its own.
        await trainingPlansService.update(id, { ...payload, apply_week_numbers: [] })
        navigate(`/training-plans/${id}`)
      } else if (coachToken) {
        // Inviting a coach via their secret link: the plan is pending until they
        // accept and build it. The token resolves the coach server-side.
        await trainingPlansService.create({ ...payload, coach_token: coachToken })
        showSuccess(t('trainingPlans.invitation.sent'))
        navigate('/training-plans')
      } else {
        // Self-managed plan: go straight to the builder.
        const created = await trainingPlansService.create(payload)
        navigate(`/training-plans/${created.id}`)
      }
    } catch {
      showError(t('trainingPlans.builder.saveError'))
      setSubmitting(false)
    }
  }

  if (!initialData) {
    return (
      <div className="max-w-2xl mx-auto flex flex-col gap-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    )
  }

  return (
    <PlanWizard
      initialData={initialData}
      coachName={coach ? coach.name || coach.username : null}
      lockShape={isEdit}
      submitLabel={t('trainingPlans.wizard.finish')}
      submitting={submitting}
      onSubmit={handleSubmit}
    />
  )
}
